/**
 * Subscribe & Save selector for the PDP buy box (WIL-28).
 *
 * Renders on products that have selling plans (Shopify Subscriptions). Lets the
 * shopper switch between One-time purchase and Subscribe & Save, pick a delivery
 * frequency, and see the discounted price live. Binds the chosen plan to the
 * product form's hidden `selling_plan` input so it carries into the cart/order;
 * clearing it (one-time) disables the input so nothing is submitted.
 *
 * Reads variant pricing (incl. selling_plan_allocations) from the same
 * `[data-product-json]` the buy box already prints, and re-reads it when the
 * variant changes so the price stays correct across variants.
 */
if (!customElements.get('ws-product-subscribe')) {
  class WsProductSubscribe extends HTMLElement {
    #abort = null;
    #observer = null;
    #product = null;

    connectedCallback() {
      this.#abort = new AbortController();
      this.#product = this.#readProduct();
      if (!this.#product) return;

      this.#refreshOptionPrices();
      this.#bind();
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#observer?.disconnect();
    }

    #readProduct() {
      try {
        const el = document.querySelector('[data-product-json]');
        return el ? JSON.parse(el.textContent) : null;
      } catch {
        return null;
      }
    }

    #bind() {
      const { signal } = this.#abort;

      for (const radio of this.querySelectorAll('[data-purchase-type]')) {
        radio.addEventListener('change', () => this.#setMode(radio.value), { signal });
      }

      this.querySelector('[data-frequency]')?.addEventListener(
        'change',
        () => {
          const input = this.querySelector('[data-selling-plan]');
          if (input && !input.disabled) input.value = this.#selectedPlanId();
          this.#refreshOptionPrices();
          if (this.#mode() === 'subscribe') this.#applyMainPrice();
        },
        { signal }
      );

      // Re-price when the buy box switches variant (ws-product-form rewrites the
      // hidden variant id's value attribute).
      const variantInput = document.querySelector('[data-variant-id]');
      if (variantInput) {
        this.#observer = new MutationObserver(() => {
          this.#refreshOptionPrices();
          this.#applyMainPrice();
        });
        this.#observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    #mode() {
      return this.querySelector('[data-purchase-type]:checked')?.value ?? 'onetime';
    }

    #selectedPlanId() {
      return this.querySelector('[data-frequency]')?.value ?? '';
    }

    #currentVariant() {
      const id = document.querySelector('[data-variant-id]')?.getAttribute('value');
      const variants = this.#product?.variants ?? [];
      return variants.find((v) => String(v.id) === String(id)) ?? variants[0] ?? null;
    }

    #allocation(variant, planId) {
      return (variant?.selling_plan_allocations ?? []).find(
        (a) => String(a.selling_plan_id) === String(planId)
      );
    }

    #money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active ?? 'USD'
      }).format(cents / 100);
    }

    /** Fill the price shown inside each option card. */
    #refreshOptionPrices() {
      const variant = this.#currentVariant();
      if (!variant) return;

      const onetime = this.querySelector('[data-onetime-price]');
      if (onetime) onetime.textContent = this.#money(variant.price);

      const subscribe = this.querySelector('[data-subscribe-price]');
      const alloc = this.#allocation(variant, this.#selectedPlanId());
      if (subscribe) subscribe.textContent = alloc ? this.#money(alloc.per_delivery_price) : '';

      // Frequency dropdown: show each plan's per-delivery price next to its label.
      const select = this.querySelector('[data-frequency]');
      if (select) {
        for (const option of select.options) {
          if (!option.dataset.label) option.dataset.label = option.textContent.trim();
          const planAlloc = this.#allocation(variant, option.value);
          option.textContent = planAlloc
            ? `${option.dataset.label} — ${this.#money(planAlloc.per_delivery_price)}`
            : option.dataset.label;
        }
      }
    }

    #setMode(mode) {
      const input = this.querySelector('[data-selling-plan]');
      if (input) {
        if (mode === 'subscribe') {
          input.disabled = false;
          input.value = this.#selectedPlanId();
        } else {
          input.disabled = true;
          input.value = '';
        }
      }
      this.#applyMainPrice();
    }

    /** Drive the buy box's main price/compare-at to reflect the current choice. */
    #applyMainPrice() {
      const variant = this.#currentVariant();
      if (!variant) return;

      const priceEl = document.querySelector('[data-price]');
      const compareEl = document.querySelector('[data-compare-price]');
      if (!priceEl) return;

      if (this.#mode() === 'subscribe') {
        const alloc = this.#allocation(variant, this.#selectedPlanId());
        if (alloc) {
          priceEl.textContent = this.#money(alloc.per_delivery_price);
          if (compareEl) {
            compareEl.textContent = this.#money(variant.price);
            compareEl.hidden = false;
          }
          return;
        }
      }

      // One-time: variant's own price and its native compare-at behaviour.
      priceEl.textContent = this.#money(variant.price);
      if (compareEl) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        compareEl.hidden = !onSale;
        compareEl.textContent = onSale ? this.#money(variant.compare_at_price) : '';
      }
    }
  }

  customElements.define('ws-product-subscribe', WsProductSubscribe);
}
